import {
  SupportsChartExport,
  useRegisterChartForExport,
  useSkipExportRenderingCallback,
} from '@ecosystem/export-utils';
import Highcharts, { Chart } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { EXPORT_CHART_SIZE, EXPORT_RENDERING_RESOLUTION } from '@hooks/export/Model';
import {
  AxesRange,
  BaseChartResp,
  BubbleChartOptionsParam,
  BubbleChartResp,
  RetailerType,
  SaleType,
} from '@models/OpportunityReview';
import { NoResult } from '@pages/NoResult';
import enableBubbleChartNegativeLogarithm from './BubbleChartNegativeLogarithm';
import { getAxesRange, getDataByRetailerType } from './OpportunityReviewBubbleChartUtil';
import { FactRevenue } from '@utils/pageConfig/Types';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionCreators } from '../../state/Index';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import { IRootState } from '@state/reducers/Reducers';
import { useExportContext } from '@hooks/export/context/ExportContext';
import { isEmpty } from '@utils/ObjectUtils';
import { useGetChartOptions } from './Options';

HighchartsMore(Highcharts);

export interface OpportunityReviewBubbleChartProps extends SupportsChartExport {
  data: BubbleChartResp;
  retailerType: RetailerType;
  selectedPeriod: string;
  selectedGrowthType: GrowthTypes;
  factRevenue: FactRevenue;
  saleType: SaleType;
  isDataChanged: boolean;
  setIsDataChanged: Dispatch<SetStateAction<boolean>>;
  isExportModalOpen: boolean;
  volume: string;
  defaultBrandBubbleForCollection?: string;
}

const OpportunityReviewBubbleChart: FC<OpportunityReviewBubbleChartProps> = ({
  data,
  retailerType,
  selectedPeriod,
  selectedGrowthType,
  factRevenue,
  saleType,
  isDataChanged,
  setIsDataChanged,
  onExportReady,
  isExportModalOpen,
  volume,
  defaultBrandBubbleForCollection,
}) => {
  const dispatch = useDispatch();
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);
  const { setChartRefsIsInitialisedStatus } = useExportContext();
  const mainFiltersState: MainFiltersState = useSelector((state: IRootState) => state.mainFilters);

  const chartComponent = useRef<Highcharts.Axis>({} as Highcharts.Axis);

  const [selectedBrandBubble, setSelectedBrandBubble] = useState(() => {
    if (defaultBrandBubbleForCollection) {
      return defaultBrandBubbleForCollection;
    }

    // Initialize from sessionStorage if exists, otherwise default to an empty string
    return mainFiltersState.selectedBrandBubble ?? '';
  });

  // to avoid security warnings from Highcharts
  Highcharts.AST?.allowedAttributes &&
    ['viewBox', 'xmlns', 'fill-rule', 'clip-rule'].forEach((attr) => {
      if (!Highcharts.AST.allowedAttributes.includes(attr)) {
        Highcharts.AST.allowedAttributes.push(attr);
      }
    });

  Highcharts.AST?.allowedTags &&
    ['title'].forEach((tag) => {
      if (!Highcharts.AST.allowedTags.includes(tag)) {
        Highcharts.AST.allowedTags.push(tag);
      }
    });

  // The chart's Y axis range is huge, so we need to make it logarithmic
  // However, logarithmic axis does not support negative values out of the box
  // This extension enables the negative values
  enableBubbleChartNegativeLogarithm(Highcharts);

  // Update sessionStorage when selectedBrandBubble changes
  useEffect(() => {
    updateMainFilters({
      selectedBrandBubble: selectedBrandBubble,
    });
  }, [selectedBrandBubble]);

  useEffect(() => {
    Highcharts.setOptions({
      lang: {
        numericSymbols: ['k', 'm', 'b', 't', 'p', 'e'],
      },
    });
  }, []);

  const dataArray: BaseChartResp[] = getDataByRetailerType(data?.content ?? [], retailerType);
  const axesRange: AxesRange = getAxesRange(dataArray);

  useEffect(() => {
    const chart: Highcharts.Chart = chartComponent.current?.chart;
    if (chart && axesRange.xAxisRange && axesRange.yAxisRange) {
      const { xAxisRange, yAxisRange } = axesRange;
      chart.xAxis[0].update(xAxisRange);
      chart.yAxis[0].update(yAxisRange);
    }
  }, [data, retailerType, selectedPeriod, selectedGrowthType, factRevenue, saleType, isExportModalOpen]);

  const chartRef = useRef<Chart | null>(null);

  const callback: Highcharts.ChartCallbackFunction = useCallback((chart) => {
    chartRef.current = chart;
  }, []);

  const chartCallback = useSkipExportRenderingCallback(callback);
  useRegisterChartForExport(chartRef, onExportReady, EXPORT_CHART_SIZE, EXPORT_RENDERING_RESOLUTION, undefined, []);

  useEffect(() => {
    // Set a status flag to indicate that the chart has been initialised. This is needed to ensure
    // that all the charts are ready for export.
    setChartRefsIsInitialisedStatus((prev) => {
      if (isEmpty(chartRef.current) || prev[0]) return prev;

      const newRefs = [...prev];
      newRefs[0] = true;
      return newRefs;
    });
  }, [chartRef.current]);

  const getBubbleChartOptionsParam: BubbleChartOptionsParam = {
    data,
    retailerType,
    selectedPeriod,
    selectedGrowthType,
    factRevenue,
    saleType,
    isDataChanged,
    setIsDataChanged,
    axesRange,
    isExportModalOpen,
    selectedBrandBubble,
    setSelectedBrandBubble,
    volume,
  };
  const options = useGetChartOptions(getBubbleChartOptionsParam);

  if (data === null || data.content.length === 0 || selectedPeriod === '') {
    return NoResult();
  }

  return (
    <>
      <HighchartsReact ref={chartComponent as any} highcharts={Highcharts} options={options} callback={chartCallback} />
      <div className="hidden" data-testid="selectedBrandBubble">
        {selectedBrandBubble}
      </div>
    </>
  );
};

export default OpportunityReviewBubbleChart;
